import { useFieldLabelsBeside } from 'components/forms/inputs/rhf-inputs/InputWrapper';
import SelectInput from 'components/forms/inputs/rhf-inputs/SelectInput';
import { useEffect, useMemo } from 'react';
import { Control, Path, PathValue, SetFieldValue, UseFormSetValue } from 'react-hook-form';
import { ClassifierData } from 'types/classifiers';
import { InvoiceEmailFormBase } from 'types/invoices';
import { getValidOptionsFromClassifier } from 'utils/formUtil';

type LanguageOption = { label: string; value: string };

interface TemplateLanguageSelectorProps<T extends InvoiceEmailFormBase> {
    control: Control<T>;
    setValue: UseFormSetValue<T>;
    languageClassifier: ClassifierData | undefined;
    availableLanguages?: string[];
    preferredLanguage?: string;
}

export const TemplateLanguageSelector = <T extends InvoiceEmailFormBase>({
    control,
    setValue,
    languageClassifier,
    availableLanguages,
    preferredLanguage,
}: TemplateLanguageSelectorProps<T>) => {
    const options = useMemo<LanguageOption[]>(
        () =>
            (getValidOptionsFromClassifier(languageClassifier) as LanguageOption[]).filter(
                (classifier) => availableLanguages && availableLanguages.includes(classifier.value)
            ) ?? [],
        [languageClassifier, availableLanguages]
    );

    const customEmailTemplateLanguageField = 'customEmailTemplateLanguage' as Path<T>;
    // SelectInput requires SetFieldValue (a looser RHF type) which TS can't
    // prove equivalent to UseFormSetValue<T> due to RHF generic invariance.
    const selectSetValue = setValue as SetFieldValue<T>;
    const labelBeside = useFieldLabelsBeside();

    // TODO(react-doctor: no-prop-callback-in-effect): `setValue` is RHF's
    // framework API for syncing form state — kept in an effect.
    useEffect(() => {
        // preferredLanguage might be CSV like "et,en", so take only the first part
        const firstLanguage = preferredLanguage?.split(',')[0]?.trim();

        if (firstLanguage && availableLanguages?.includes(firstLanguage)) {
            const preferredOption = options.find((option) => option.value === firstLanguage);
            if (preferredOption) {
                setValue(customEmailTemplateLanguageField, preferredOption as PathValue<T, Path<T>>);
            }
        }
    }, [preferredLanguage, availableLanguages, options, setValue, customEmailTemplateLanguageField]);

    return (
        <div className={labelBeside ? undefined : 'pt-2'}>
            <SelectInput<T, LanguageOption>
                setValue={selectSetValue}
                control={control}
                label="invoices.email.template-lang"
                name={customEmailTemplateLanguageField}
                options={options}
                getOptionLabel="label"
                getOptionIdentifier="value"
                placeholder="invoices.email.template-lang-tooltip"
            />
        </div>
    );
};
